// bluaIQ — M5: Budget Module
// Version-controlled budgets by phase with line items

import { apiFetch } from '../utils/api'
import { parseMoneyToCents } from '../shared/moneyPrecision'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { DollarSign, Plus, TrendingUp, BarChart3, Trash2, Download, AlertTriangle, Info, Pencil } from 'lucide-react'
import { downloadCsv } from '../utils/exportCsv'
import { isDemoProject } from '../utils/demoData'
// Financial Trust P0 (PR-F2): reuse the safe money/percent helpers from PR-F1 so
// Budget never renders NaN/Infinity/null as money or an absurd raw percentage.
import { formatMoney, formatMoneyValue, formatPercent, formatPercentRatio } from '../utils/money'
// D-25AGO-09 — ONE canonicalization for budget money, shared with the server.
// The value shown in the confirmation IS the value put on the wire IS the value
// the server accepts IS the value persisted; there is no second conversion.
import { canonicalizeBudgetAmountInput } from '../services/budgetVersioning'
import { useTakeoffAvailable } from '../takeoff-diagnostic-v3/TakeoffAvailabilityContext'
import { formatMoneyFromCents, sumCents } from '../shared/moneyPrecision'

// ── TYPES ─────────────────────────────────────────

interface BudgetItem {
  id: string
  category: string
  label: string
  totalCost: number
}

interface BudgetVersionSummary {
  id: string
  version: number
  totalCost: number
  notes?: string | null
  createdAt: string
  lineCount: number
  isCurrent: boolean
}

interface Budget {
  id: string
  version: number
  totalCost: number
  notes?: string
  createdAt: string
  lineItems: BudgetItem[]
}

const BUDGET_CATEGORIES: Record<string, { en: string; es: string; color: string }> = {
  LAND:           { en: 'Land/Lot',           es: 'Terreno',            color: '#06B6D4' },
  PERMITS:        { en: 'Permits & Fees',     es: 'Permisos y Cuotas',  color: '#3B82F6' },
  FOUNDATION:     { en: 'Foundation',          es: 'Cimentación',        color: '#00BCD4' },
  FRAMING:        { en: 'Framing',             es: 'Estructura',         color: '#14B8A6' },
  ROOFING:        { en: 'Roofing',             es: 'Techo',              color: '#EC4899' },
  MEP:            { en: 'MEP (Mech/Elec/Plumb)', es: 'MEP (Mec/Elec/Plom)', color: '#EF4444' },
  INSULATION:     { en: 'Insulation',          es: 'Aislamiento',        color: '#F59E0B' },
  DRYWALL:        { en: 'Drywall',             es: 'Tablaroca',          color: '#22C55E' },
  FINISHES:       { en: 'Finishes',            es: 'Acabados',           color: '#6366F1' },
  LANDSCAPING:    { en: 'Landscaping',         es: 'Paisajismo',         color: '#10B981' },
  CONTINGENCY:    { en: 'Contingency',         es: 'Contingencia',       color: '#9E9890' },
  OTHER:          { en: 'Other',               es: 'Otro',               color: '#94A3B8' },
}

// ── BUDGET SOURCE HONESTY (PR-F2) ─────────────────
// The schema has NO per-line source field (BudgetItem = id/category/label/amount),
// and the ONLY backend path that creates budget items is the manual POST
// /projects/:id/budget (AddBudgetItemForm). There is NO takeoff→budget auto-gen and
// the module does not read contracts/quotes/bids/takeoff. So today: every line's
// source is genuinely "unknown" (manual by creation path), and committed/spent/
// quotes/bids are tracked in their own modules — NOT linked into this budget.
// These labels make that honest without faking integration or changing schema.
type BudgetSource = 'manual' | 'takeoff' | 'quote' | 'bid' | 'contract' | 'receipt' | 'changeOrder' | 'unknown'

const BUDGET_SOURCE_LABELS: Record<BudgetSource, { en: string; es: string }> = {
  manual:      { en: 'Manual',            es: 'Manual' },
  takeoff:     { en: 'Takeoff estimate',  es: 'Estimado de takeoff' },
  quote:       { en: 'Supplier quote',    es: 'Cotización de proveedor' },
  bid:         { en: 'Contractor bid',    es: 'Propuesta de contratista' },
  contract:    { en: 'Contract',          es: 'Contrato' },
  receipt:     { en: 'Receipt / expense', es: 'Recibo / gasto' },
  changeOrder: { en: 'Change order',      es: 'Orden de cambio' },
  unknown:     { en: 'Source unknown',    es: 'Fuente desconocida' },
}

const BUDGET_TRUST_COPY = {
  needsReview:          { en: 'Needs review',           es: 'Requiere revisión' },
  budgetSourceMissing:  { en: 'Budget source missing',  es: 'Falta fuente del presupuesto' },
  notConnected:         { en: 'Not connected yet',      es: 'Aún no conectado' },
  trackedSeparately:    { en: 'Tracked separately',     es: 'Registrado por separado' },
  inBudget:             { en: 'In budget',              es: 'En presupuesto' },
} as const

// Resolve a line item's source. No source column exists yet, so this returns
// 'unknown' today — but it reads an optional source/sourceType first so a future
// Project Cost Sources layer resolves without further UI changes.
function budgetLineSource(item: BudgetItem): BudgetSource {
  const raw = (item as { source?: string; sourceType?: string }).source
    ?? (item as { source?: string; sourceType?: string }).sourceType
  if (typeof raw === 'string' && raw in BUDGET_SOURCE_LABELS && raw !== 'unknown') {
    return raw as BudgetSource
  }
  return 'unknown'
}

function sourceLabel(src: BudgetSource, isEs: boolean): string {
  return isEs ? BUDGET_SOURCE_LABELS[src].es : BUDGET_SOURCE_LABELS[src].en
}
function trustLabel(key: keyof typeof BUDGET_TRUST_COPY, isEs: boolean): string {
  return isEs ? BUDGET_TRUST_COPY[key].es : BUDGET_TRUST_COPY[key].en
}

// ── COMPONENT ─────────────────────────────────────

export default function BudgetModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'
  const [showAddItem, setShowAddItem] = useState(false)
  // D-25AGO-09 — editing existing lines. Budget was append-only: the only
  // budgetItem calls in the whole server were .create and .findMany, so a typo in
  // a line was permanent. An edit is a NEW VERSION, never an in-place mutation.
  const [editingLines, setEditingLines] = useState(false)
  // D-25AGO-09 — prior versions are retained AND readable. `null` = the current
  // version (the default); a number = a HISTORICAL, read-only view.
  const [viewVersion, setViewVersion] = useState<number | null>(null)

  const { data: apiBudget, isLoading } = useQuery<Budget>({
    queryKey: ['budget', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/budget`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })

  // The version index. Cheap (no line items) and only meaningful once a budget
  // exists, so it is skipped on demo projects, which have no rows to list.
  const { data: apiVersions } = useQuery<BudgetVersionSummary[]>({
    queryKey: ['budget-versions', projectId],
    enabled: !isDemoProject(projectId),
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/budget/versions`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => r.ok ? r.json() : []) },
    retry: false,
  })

  // One historical version, fetched only while the user is actually looking at
  // one. Read-only: there is no mutation keyed on this query.
  const { data: apiHistorical, isLoading: historicalLoading } = useQuery<Budget>({
    queryKey: ['budget-version', projectId, viewVersion],
    enabled: viewVersion !== null,
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/budget/versions/${viewVersion}`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('version unavailable')
      return r.json()
    }) },
    retry: false,
  })

  const { data: apiReceipts } = useQuery<any[]>({
    queryKey: ['receipts', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/receipts`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => r.ok ? r.json() : []) },
    retry: false,
  })

  // Demo fallback — only for known demo projects, NOT real projects.
  // Real projects with no budget should show $0, not hardcoded totals.
  const DEMO_BUDGET_FALLBACK = {
    id: 'demo-b1', version: 3, totalCost: 285000, createdAt: new Date().toISOString(), lineItems: [
      { id: 'bl1', category: 'LAND', label: 'Terreno', totalCost: 65000 },
      { id: 'bl2', category: 'FOUNDATION', label: 'Cimentación', totalCost: 18500 },
      { id: 'bl3', category: 'FRAMING', label: 'Framing + materiales', totalCost: 42000 },
      { id: 'bl4', category: 'ROOFING', label: 'Techo', totalCost: 12800 },
      { id: 'bl5', category: 'MEP', label: 'Plomería + Eléctrico + HVAC', totalCost: 38000 },
      { id: 'bl6', category: 'INSULATION', label: 'Aislamiento', totalCost: 5200 },
      { id: 'bl7', category: 'DRYWALL', label: 'Drywall + Pintura', totalCost: 16500 },
      { id: 'bl8', category: 'FINISHES', label: 'Acabados (pisos, baños, cocina)', totalCost: 45000 },
      { id: 'bl9', category: 'EXTERIOR', label: 'Exterior (siding, landscaping)', totalCost: 22000 },
      { id: 'bl10', category: 'PERMITS', label: 'Permisos y fees', totalCost: 8500 },
      { id: 'bl11', category: 'CONTINGENCY', label: 'Contingencia (5%)', totalCost: 11500 },
    ]
  }
  const currentBudget = apiBudget || (isDemoProject(projectId) ? DEMO_BUDGET_FALLBACK : null)
  // Everything below renders the SELECTED version. When a historical one is
  // selected every mutation affordance is withheld, so an old version cannot be
  // edited into by accident — the write routes only ever target the latest.
  const isHistoricalView = viewVersion !== null
  const budget = isHistoricalView ? (apiHistorical ?? null) : currentBudget

  if (isLoading) return <div className="p-5 animate-pulse"><div className="h-64 bg-[#ECEAE4] rounded-xl" /></div>

  const lineItems = budget?.lineItems || []
  const categories = [...new Set(lineItems.map(i => i.category))]
  const totalCost = budget?.totalCost || lineItems.reduce((sum, i) => sum + i.totalCost, 0)
  // PR-F2: trust-guard the headline total. A finite total (incl. a small real $33)
  // is preserved; null/NaN/Infinity → "Unavailable" instead of $NaN.
  const totalCostMoney = formatMoney(totalCost, { isEs })
  // Safe denominator for share-of-total math (bar widths + category %): only a
  // finite, positive total can produce a meaningful share.
  const totalIsUsableDenominator = Number.isFinite(totalCost) && totalCost > 0
  // Every line's source is "unknown" today (no source column) — surfaced honestly.
  const allLinesUnknownSource = lineItems.length > 0 && lineItems.every(i => budgetLineSource(i) === 'unknown')

  // Over-budget detection: compare receipt spending by category vs budget
  const receipts = Array.isArray(apiReceipts) ? apiReceipts : []
  const spentByCategory: Record<string, number> = {}
  receipts.forEach(r => {
    (r.items || []).forEach((item: any) => {
      const cat = item.category || 'OTHER'
      spentByCategory[cat] = (spentByCategory[cat] || 0) + (item.total || item.totalPrice || 0)
    })
  })
  const overBudgetItems = lineItems
    .map(item => {
      const spent = spentByCategory[item.category] || 0
      const pct = item.totalCost > 0 ? Math.round((spent / item.totalCost) * 100) : 0
      return { ...item, spent, pct }
    })
    .filter(item => item.pct > 120)

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{t('Budget')}</h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            v{budget?.version || 1} · {lineItems.length} {t('line items')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lineItems.length > 0 && (
            <button
              onClick={() => downloadCsv({
                filename: `presupuesto-v${budget?.version || 1}`,
                headers: [isEs ? 'Categoría' : 'Category', isEs ? 'Descripción' : 'Description', isEs ? 'Monto' : 'Amount'],
                rows: [...lineItems.map(i => [i.category, i.label, i.totalCost]), ['', isEs ? 'TOTAL' : 'TOTAL', totalCost]]
              })}
              className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors"
            >
              <Download size={14} />
              CSV
            </button>
          )}
          {/* D-25AGO-09 — the missing edit affordance. Hidden on demo projects
              (no rows in the database to supersede) and when there is nothing to
              edit yet. */}
          {lineItems.length > 0 && !isDemoProject(projectId) && !editingLines && !isHistoricalView && (
            <button
              data-testid="budget-edit-lines-btn"
              onClick={() => setEditingLines(true)}
              className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors"
            >
              <Pencil size={14} />
              {isEs ? 'Editar partidas' : 'Edit lines'}
            </button>
          )}
          {/* Withheld while a HISTORICAL version is on screen: a past version is
              read-only, and there must be no affordance that looks like editing
              into it. */}
          {!isHistoricalView && (
            <button
              data-testid="budget-add-item-btn"
              onClick={() => setShowAddItem(true)}
              className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full"
            >
              <Plus size={14} />
              {t('Add Item')}
            </button>
          )}
        </div>
      </div>

      {/* D-25AGO-09 — prior versions are retained AND readable. Without this the
          spec's "superseding is reversible because the prior version stays
          readable" was only half true: the rows existed and nothing could reach
          them. Read-only by construction — no verb anywhere in this panel. */}
      {!isDemoProject(projectId) && (apiVersions?.length ?? 0) > 1 && (
        <BudgetVersionHistory
          isEs={isEs}
          versions={apiVersions ?? []}
          viewVersion={viewVersion}
          onSelect={(v) => { setViewVersion(v); setShowAddItem(false); setEditingLines(false) }}
        />
      )}

      {isHistoricalView && (
        <div
          data-testid="budget-historical-banner"
          className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-[#F5E2B8] bg-[#FEF7E6] px-3 py-2.5"
        >
          <div className="flex-1 text-[11px] font-[700] text-[#9E6B00] leading-relaxed">
            {isEs
              ? `Estás viendo la versión ${viewVersion} — es historial, solo lectura. No se puede editar; los cambios siempre crean una versión nueva a partir de la actual.`
              : `You are viewing version ${viewVersion} — this is history, read-only. It cannot be edited; changes always create a new version from the current one.`}
          </div>
          <button
            type="button"
            data-testid="budget-back-to-current-btn"
            onClick={() => setViewVersion(null)}
            className="w-full sm:w-auto min-h-[44px] whitespace-nowrap rounded-full border border-[#D6CFC4] bg-white px-4 text-[11px] font-[800] text-[#6B655C]"
          >
            {isEs ? 'Volver a la versión actual' : 'Back to current version'}
          </button>
        </div>
      )}

      {isHistoricalView && historicalLoading && (
        <div className="animate-pulse"><div className="h-24 bg-[#ECEAE4] rounded-xl" /></div>
      )}

      {/* Add item form — available from both empty state and populated view */}
      {showAddItem && (
        <AddBudgetItemForm
          projectId={projectId!}
          isEs={isEs}
          // D-25AGO-09: once a budget EXISTS, adding a line is a version
          // supersede like any other line change — never a direct append.
          currentBudget={apiBudget ?? null}
          onClose={() => setShowAddItem(false)}
          onAdded={() => {
            setShowAddItem(false)
            queryClient.invalidateQueries({ queryKey: ['budget', projectId] })
            queryClient.invalidateQueries({ queryKey: ['budget-versions', projectId] })
          }}
        />
      )}

      {/* Empty state — no budget rows in DB */}
      {!budget || lineItems.length === 0 ? (
        !showAddItem && (
          <div className="bg-white border border-[#ECEAE4] rounded-xl p-10 text-center">
            <DollarSign size={36} className="mx-auto text-[#ECEAE4] mb-3" />
            {/* PR-F2 source honesty: the previous copy promised "Approve a takeoff
                to auto-generate the budget" — there is NO such auto-generation
                (budget items are created manually only). Don't advertise a
                connection that doesn't exist. */}
            <p className="text-[12px] font-[700] text-[#9E9890] max-w-sm mx-auto">
              {isEs
                ? 'Aún no hay presupuesto. Agrega partidas manualmente para empezar.'
                : 'No budget yet. Add budget line items manually to get started.'}
            </p>
            <p className="text-[10px] font-[600] text-[#9E9890] max-w-sm mx-auto mt-2 leading-relaxed">
              {isEs
                ? 'Takeoff, cotizaciones, propuestas y contratos se registran por separado y aún no se vinculan al presupuesto automáticamente.'
                : 'Takeoff, quotes, bids and contracts are tracked separately and are not linked into the budget automatically yet.'}
            </p>
          </div>
        )
      ) : (
        <>
          {/* Total KPI — PR-F2: safe money + honest scope (budget line items only;
              committed/spent live in their own modules and aren't summed here). */}
          <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 border-l-[3px] border-l-[#06539B]">
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{t('Total Budget')}</div>
            <div data-testid="budget-total-value" className={`text-[28px] font-[900] tracking-[-1px] ${totalCostMoney.isValid ? 'text-[#1C1A17]' : 'text-[#9E9890]'}`}>
              {totalCostMoney.displayValue}
            </div>
            <div data-testid="budget-total-scope" className="text-[10px] font-[700] text-[#9E9890] mt-1">
              {isEs
                ? 'Solo partidas del presupuesto — comprometido y gastado se registran por separado.'
                : 'Budget line items only — committed and spent are tracked separately.'}
            </div>
          </div>

          {/* PR-F2: Budget Sources trust panel — honest about source + what is NOT
              yet linked. No cross-module writes, no faked integration. */}
          <BudgetSourcesPanel isEs={isEs} allLinesUnknownSource={allLinesUnknownSource} />

          {/* D-25AGO-09 — edit existing lines by superseding this version. */}
          {editingLines && budget && (
            <BudgetVersionEditor
              projectId={projectId!}
              isEs={isEs}
              baseVersion={budget.version || 1}
              lineItems={lineItems}
              onClose={() => setEditingLines(false)}
              onSaved={() => {
                setEditingLines(false)
                queryClient.invalidateQueries({ queryKey: ['budget', projectId] })
              }}
            />
          )}

          {/* Over-budget alerts */}
          {overBudgetItems.length > 0 && (
            <div className="space-y-2">
              {overBudgetItems.map(item => (
                <div key={item.id} className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-red-200 bg-red-50">
                  <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                  <span className="text-[11px] font-[700] text-red-800 flex-1">
                    {/* PR-F2: safe money + capped % so a tiny budget line vs large
                        spend shows ">999%", never a giant raw number. */}
                    <strong>{item.label}</strong> — {t('over budget')}: {formatMoneyValue(item.spent, isEs)} / {formatMoneyValue(item.totalCost, isEs)} ({formatPercentRatio(item.spent, item.totalCost, { isEs }).displayValue})
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Category breakdown */}
          <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-[#ECEAE4] flex items-center">
              <BarChart3 size={14} className="text-[#9E9890] mr-2" />
              <h3 className="text-[13px] font-[800] flex-1">{t('Budget Breakdown')}</h3>
            </div>

            {/* Stacked bar */}
            <div className="px-4 py-3 border-b border-[#ECEAE4]">
              <div className="h-[12px] rounded-full overflow-hidden flex">
                {categories.map(cat => {
                  const catTotal = lineItems.filter(i => i.category === cat).reduce((s, i) => s + i.totalCost, 0)
                  // PR-F2: NaN-safe, clamped width (an invalid total → 0 width, not NaN).
                  const widthPct = totalIsUsableDenominator && Number.isFinite(catTotal)
                    ? Math.min(100, Math.max(0, (catTotal / totalCost) * 100))
                    : 0
                  return (
                    <div
                      key={cat}
                      className="h-full transition-all"
                      style={{ width: `${widthPct}%`, background: BUDGET_CATEGORIES[cat]?.color || '#94A3B8' }}
                      title={`${isEs ? BUDGET_CATEGORIES[cat]?.es : BUDGET_CATEGORIES[cat]?.en}: ${formatMoneyValue(catTotal, isEs)}`}
                    />
                  )
                })}
              </div>
            </div>

            {/* Line items by category */}
            {categories.map(cat => {
              const catItems = lineItems.filter(i => i.category === cat)
              const catTotal = catItems.reduce((s, i) => s + i.totalCost, 0)
              const catLabel = BUDGET_CATEGORIES[cat]
              // PR-F2: share-of-total via the denominator-safe ratio helper (caps + "—").
              const catPct = formatPercentRatio(catTotal, totalCost, { isEs }).displayValue

              return (
                <div key={cat} className="border-t border-[#ECEAE4]">
                  <div className="flex items-center gap-2 px-4 py-2.5">
                    <div className="w-[8px] h-[8px] rounded-[2px]" style={{ background: catLabel?.color || '#94A3B8' }} />
                    <span className="text-[12px] font-[700] flex-1">{isEs ? catLabel?.es : catLabel?.en || cat}</span>
                    <span className="text-[10px] font-[800] text-[#9E9890]">{catPct}</span>
                    <span className="text-[12px] font-[800] text-[#1C1A17] w-[90px] text-right">{formatMoneyValue(catTotal, isEs)}</span>
                  </div>
                  {catItems.map(item => {
                    const src = budgetLineSource(item)
                    return (
                      <div key={item.id} className="flex items-center gap-2 px-4 py-1.5 pl-8">
                        <span className="text-[11px] font-[600] flex-1 text-[#9E9890] truncate">{item.label}</span>
                        {/* PR-F2: honest per-line source — "Source unknown" today (no
                            source column). Resolves automatically if a source is ever stamped. */}
                        <span
                          data-testid="budget-line-source"
                          className="text-[8px] font-[800] uppercase tracking-[0.3px] text-[#9E9890] bg-[#F2F0EB] rounded px-1.5 py-0.5 whitespace-nowrap"
                          title={isEs ? 'Fuente de la partida' : 'Line item source'}
                        >
                          {sourceLabel(src, isEs)}
                        </span>
                        <span className="text-[11px] font-[700] text-[#1C1A17]">{formatMoneyValue(item.totalCost, isEs)}</span>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

// ── BUDGET SOURCES PANEL (PR-F2) ──────────────────
// Honest, read-only disclosure of where the budget total comes from and what is
// NOT yet linked. No data is written; no integration is implied beyond reality.
function BudgetSourcesPanel({ isEs, allLinesUnknownSource }: { isEs: boolean; allLinesUnknownSource: boolean }) {
  // Connection status today (no cross-module linking, no source column):
  //  - manual line items ARE the budget total ("In budget")
  //  - takeoff/quote/bid/changeOrder exist elsewhere but feed nothing → "Not connected yet"
  //  - contract/receipt are surfaced on the Dashboard but not summed here → "Tracked separately"
  // Cuantificación switch: the takeoff row is kept (takeoff is the baseline
  // to compare budgets against) but only shown while takeoff is switched on.
  const takeoffAvailable = useTakeoffAvailable()
  const rows: { src: BudgetSource; statusKey: keyof typeof BUDGET_TRUST_COPY }[] = [
    { src: 'manual',      statusKey: 'inBudget' },
    ...(takeoffAvailable ? [{ src: 'takeoff' as BudgetSource, statusKey: 'notConnected' as const }] : []),
    { src: 'quote',       statusKey: 'notConnected' },
    { src: 'bid',         statusKey: 'notConnected' },
    { src: 'contract',    statusKey: 'trackedSeparately' },
    { src: 'receipt',     statusKey: 'trackedSeparately' },
    { src: 'changeOrder', statusKey: 'notConnected' },
  ]
  return (
    <div data-testid="budget-sources-panel" className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-[#ECEAE4] flex items-center gap-2">
        <Info size={14} className="text-[#9E9890]" />
        <h3 className="text-[13px] font-[800] flex-1">{isEs ? 'Fuentes del Presupuesto' : 'Budget Sources'}</h3>
      </div>

      {/* Manual-only honesty: the only creation path is manual entry. */}
      {allLinesUnknownSource && (
        <div data-testid="budget-manual-only-note" className="px-4 py-2.5 text-[11px] font-[700] text-[#1C1A17] border-b border-[#F4F2EE]">
          {isEs
            ? 'Este presupuesto actualmente incluye solo partidas manuales.'
            : 'This budget currently includes manual budget lines only.'}
        </div>
      )}

      {/* Not-connected honesty: committed/spent and other sources are separate. */}
      <div data-testid="budget-not-connected-note" className="px-4 py-2.5 text-[10px] font-[600] text-[#9E6B00] bg-[#FEF7E6] border-b border-[#F5E2B8] leading-relaxed">
        {isEs
          ? 'Contratos y recibos se registran por separado. La conexión de fuentes al presupuesto aún no está conectada.'
          : 'Contracts and receipts are tracked separately. Budget source linking is not connected yet.'}
      </div>

      {/* Source taxonomy + per-source connection status (static, honest today). */}
      <div className="divide-y divide-[#F4F2EE]">
        {rows.map(({ src, statusKey }) => (
          <div key={src} data-testid="budget-source-row" className="flex items-center gap-2 px-4 py-1.5">
            <span className="text-[11px] font-[700] flex-1 text-[#1C1A17]">{sourceLabel(src, isEs)}</span>
            <span className={`text-[9px] font-[800] uppercase tracking-[0.3px] rounded px-1.5 py-0.5 ${
              statusKey === 'inBudget'
                ? 'bg-emerald-100 text-emerald-700'
                : statusKey === 'trackedSeparately'
                  ? 'bg-sky-100 text-sky-700'
                  : 'bg-[#F2F0EB] text-[#9E9890]'
            }`}>
              {trustLabel(statusKey, isEs)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── ADD ITEM FORM ─────────────────────────────────

function AddBudgetItemForm({ projectId, isEs, currentBudget, onClose, onAdded }: {
  projectId: string; isEs: boolean; currentBudget: Budget | null; onClose: () => void; onAdded: () => void
}) {
  const { getToken } = useAuth()
  const { t } = useTranslation()
  const [form, setForm] = useState({ category: 'FRAMING', label: '', totalCost: '' })
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)

  // D-25AGO-09 — the money the user typed, canonicalized ONCE, here. This exact
  // string is what is shown below and what goes on the wire.
  const canonical = canonicalizeBudgetAmountInput(form.totalCost)

  const save = async () => {
    if (!canonical.ok) {
      setError(L(BUDGET_EDIT_COPY.errInvalid))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const token = await getToken()
      const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' }
      const newLine = { category: form.category, label: form.label.trim(), amount: canonical.amount }

      // ONE MUTATION MODEL. With no budget yet this is the initial creation; with
      // a budget in hand it is a version supersede carrying the COMPLETE desired
      // line set. Appending straight into the current version — what this form
      // used to do — is what made the editor's stale-write guard bypassable and
      // let a just-added line vanish from the published budget.
      const res = currentBudget
        ? await apiFetch(`/api/projects/${projectId}/budget/versions`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              baseVersion: currentBudget.version,
              lineItems: [
                ...currentBudget.lineItems.map(existing => {
                  const money = canonicalizeBudgetAmountInput(existing.totalCost)
                  return {
                    sourceId: existing.id,
                    category: existing.category,
                    label: existing.label,
                    // A stored line that cannot canonicalize is sent as-is and
                    // refused by the server with its index, never silently zeroed.
                    amount: money.ok ? money.amount : String(existing.totalCost),
                  }
                }),
                newLine,
              ],
            }),
          })
        : await apiFetch(`/api/projects/${projectId}/budget`, {
            method: 'POST',
            headers,
            // The legacy initial-create body keeps its numeric alias.
            body: JSON.stringify({ lineItems: [{ category: newLine.category, label: newLine.label, totalCost: canonical.cents / 100 }] }),
          })

      const body = await res.json().catch(() => ({} as any))
      if (!res.ok) {
        setError(localizeBudgetVersionError(typeof body?.code === 'string' ? body.code : '', isEs))
        return
      }
      onAdded()
    } catch {
      setError(L(BUDGET_EDIT_COPY.errGeneric))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
      <h3 className="text-[13px] font-[800]">{t('Add Budget Item')}</h3>
      <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
        className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]">
        {Object.entries(BUDGET_CATEGORIES).map(([key, val]) => (
          <option key={key} value={key}>{isEs ? val.es : val.en}</option>
        ))}
      </select>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input placeholder={t('Description')} value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
        <input placeholder={t('Amount')} type="number" step="0.01" value={form.totalCost} onChange={e => setForm({ ...form, totalCost: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
      </div>

      {/* The canonical value, shown BEFORE saving. What is displayed here is
          byte-for-byte what is sent, accepted and persisted — so a sub-cent entry
          is normalized in front of the user, never silently on the server. */}
      {canonical.ok && (
        <p data-testid="budget-add-canonical-amount" className="text-[10px] font-[700] text-[#6B655C]">
          {L(BUDGET_EDIT_COPY.willBeSaved)}: <strong className="text-[#1C1A17]">{formatMoneyFromCents(canonical.cents)}</strong>
        </p>
      )}
      {currentBudget && (
        <p data-testid="budget-add-version-note" className="text-[10px] font-[600] text-[#9E9890]">
          {L(BUDGET_EDIT_COPY.newVersion)}: v{currentBudget.version} → v{currentBudget.version + 1}
        </p>
      )}
      {error && <p data-testid="budget-add-error" className="text-[10px] font-[800] text-[#B4232A]">{error}</p>}

      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={onClose} disabled={saving}
          className="w-full min-h-[44px] border border-[#ECEAE4] rounded-full text-[11px] font-[800] text-[#9E9890]">{t('Cancel')}</button>
        <button data-testid="budget-add-save-btn" onClick={save} disabled={!form.label.trim() || !canonical.ok || saving}
          className="w-full min-h-[44px] bg-[#06539B] text-white rounded-full text-[11px] font-[800] disabled:opacity-40">
          {saving ? L(BUDGET_EDIT_COPY.savingBtn) : t('Save')}
        </button>
      </div>
    </div>
  )
}

// ── D-25AGO-09: VERSION HISTORY (READ-ONLY) ──────────────────────────────────
//
// Retention is not readability. Superseding a version is called reversible
// because the prior version is kept AND can be read; before this panel existed,
// every prior version was persisted and reachable by nothing — `GET /budget`
// returns only the highest version, so the history was invisible.
//
// Deliberately NOT implemented here: hard-delete of a version (PROHIBITED), and
// restore-as-current (a separate decision — a restore is itself a new version).
// This panel has no verb at all; selecting a version only changes what is shown.
const BUDGET_HISTORY_COPY = {
  title: { en: 'Version history', es: 'Historial de versiones' },
  subtitle: {
    en: 'Every change creates a new version. Earlier versions are kept and stay readable.',
    es: 'Cada cambio crea una versión nueva. Las versiones anteriores se conservan y siguen disponibles.',
  },
  current: { en: 'Current', es: 'Actual' },
  historical: { en: 'History', es: 'Historial' },
  lines: { en: 'lines', es: 'partidas' },
  view: { en: 'View', es: 'Ver' },
  viewing: { en: 'Viewing', es: 'Viendo' },
} as const

function BudgetVersionHistory({ isEs, versions, viewVersion, onSelect }: {
  isEs: boolean
  versions: BudgetVersionSummary[]
  viewVersion: number | null
  onSelect: (version: number | null) => void
}) {
  const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString(isEs ? 'es-MX' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' })

  return (
    <div data-testid="budget-version-history" className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-[#ECEAE4]">
        <h3 className="text-[13px] font-[800] text-[#1C1A17]">{L(BUDGET_HISTORY_COPY.title)}</h3>
        <p className="text-[10px] font-[600] text-[#9E9890] mt-0.5 leading-relaxed">{L(BUDGET_HISTORY_COPY.subtitle)}</p>
      </div>
      <div className="divide-y divide-[#F4F2EE]">
        {versions.map(v => {
          // `null` means "the current version", so the current row is selected
          // whenever no historical version is being viewed.
          const isSelected = v.isCurrent ? viewVersion === null : viewVersion === v.version
          return (
            <div
              key={v.id}
              data-testid={`budget-version-row-${v.version}`}
              className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-2.5"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-[12px] font-[800] text-[#1C1A17] whitespace-nowrap">v{v.version}</span>
                <span className={`text-[8px] font-[800] uppercase tracking-[0.3px] rounded px-1.5 py-0.5 whitespace-nowrap ${
                  v.isCurrent ? 'bg-emerald-100 text-emerald-700' : 'bg-[#F2F0EB] text-[#9E9890]'
                }`}>
                  {v.isCurrent ? L(BUDGET_HISTORY_COPY.current) : L(BUDGET_HISTORY_COPY.historical)}
                </span>
                <span className="text-[10px] font-[600] text-[#9E9890] truncate">
                  {formatDate(v.createdAt)} · {v.lineCount} {L(BUDGET_HISTORY_COPY.lines)}
                </span>
              </div>
              <span className="text-[12px] font-[800] text-[#1C1A17] whitespace-nowrap sm:w-[110px] sm:text-right">
                {formatMoneyValue(v.totalCost, isEs)}
              </span>
              <button
                type="button"
                data-testid={`budget-version-view-btn-${v.version}`}
                disabled={isSelected}
                onClick={() => onSelect(v.isCurrent ? null : v.version)}
                className="w-full sm:w-auto min-h-[44px] whitespace-nowrap rounded-full border border-[#ECEAE4] px-4 text-[11px] font-[800] text-[#1C1A17] disabled:opacity-40 disabled:cursor-default"
              >
                {isSelected ? L(BUDGET_HISTORY_COPY.viewing) : L(BUDGET_HISTORY_COPY.view)}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── D-25AGO-09: EDIT EXISTING LINES BY SUPERSEDING THE VERSION ────────────────
//
// Budget was append-only — there was no PATCH, no PUT and no DELETE for a budget
// line anywhere in the server, and no per-row affordance here. This is the
// missing edit path, and it is deliberately NOT a per-line PATCH:
//
//   • editing a line in place would restate a number prior decisions were made
//     against, and rewrite a version somebody has already read;
//   • `Budget.version` already existed and was written-but-never-incremented;
//   • every prior version stays intact and readable, which is what makes the
//     edit reversible — hard-deleting a version is PROHIBITED.
//
// So the editor composes the complete desired line set and posts it as the NEXT
// version. Removing a line here removes it from the NEW version only.
//
// The total shown is a LOCAL PREVIEW for the confirmation. The authoritative
// total is derived server-side from the line set inside the transaction; this
// component never sends a total.
const BUDGET_EDIT_COPY = {
  title: { en: 'Edit budget lines', es: 'Editar partidas del presupuesto' },
  body: {
    en: 'Saving creates a new budget version. The current version is kept and stays readable — nothing is overwritten or deleted.',
    es: 'Guardar crea una nueva versión del presupuesto. La versión actual se conserva y sigue disponible — nada se sobrescribe ni se elimina.',
  },
  categoryLabel: { en: 'Category', es: 'Categoría' },
  descriptionLabel: { en: 'Description', es: 'Descripción' },
  amountLabel: { en: 'Amount', es: 'Monto' },
  removeLine: { en: 'Remove', es: 'Quitar' },
  addLine: { en: 'Add line', es: 'Agregar partida' },
  deltaTitle: { en: 'What changes', es: 'Qué cambia' },
  added: { en: 'added', es: 'agregadas' },
  changed: { en: 'changed', es: 'modificadas' },
  removed: { en: 'removed', es: 'quitadas' },
  lines: { en: 'lines', es: 'partidas' },
  totalFromTo: { en: 'Total', es: 'Total' },
  newVersion: { en: 'New version', es: 'Nueva versión' },
  // Shown next to a typed amount BEFORE saving: the canonical value that will
  // actually be stored, so a sub-cent entry is normalized in front of the user.
  willBeSaved: { en: 'Will be saved as', es: 'Se guardará como' },
  // Shown when Save found a visible amount that was not yet its canonical form.
  // The values on screen have just been corrected; nothing was persisted.
  normalizedReviewAgain: {
    en: 'Amounts were rounded to the nearest cent. Check the updated line values, then save again.',
    es: 'Los montos se redondearon al centavo más cercano. Revisa los valores actualizados y guarda de nuevo.',
  },
  errOverflow: {
    en: 'An amount is larger than this budget can store. Use a smaller value.',
    es: 'Un monto es mayor de lo que este presupuesto puede almacenar. Usa un valor menor.',
  },
  errBudgetExists: {
    en: 'This budget already exists — reload so your change is saved as a new version.',
    es: 'Este presupuesto ya existe — recarga para que tu cambio se guarde como una nueva versión.',
  },
  saveCta: { en: 'Save as new version', es: 'Guardar como nueva versión' },
  cancelCta: { en: 'Cancel', es: 'Cancelar' },
  savingBtn: { en: 'Saving…', es: 'Guardando…' },
  errStale: {
    en: 'This budget changed while you were editing. Reload and try again — your version was not saved.',
    es: 'Este presupuesto cambió mientras editabas. Recarga e inténtalo de nuevo — tu versión no se guardó.',
  },
  errInvalid: {
    en: 'A line is incomplete or its amount is not a valid money value. Fix it and try again.',
    es: 'Una partida está incompleta o su monto no es un valor válido. Corrígelo e inténtalo de nuevo.',
  },
  errEmpty: {
    en: 'A budget version needs at least one line.',
    es: 'Una versión del presupuesto necesita al menos una partida.',
  },
  errGeneric: {
    en: 'Could not save the new budget version. Please try again.',
    es: 'No se pudo guardar la nueva versión del presupuesto. Inténtalo de nuevo.',
  },
} as const

// Map a version-save error CODE to localized copy — never the raw server string.
function localizeBudgetVersionError(code: string, isEs: boolean): string {
  const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
  switch (code) {
    case 'BUDGET_VERSION_STALE':
      return L(BUDGET_EDIT_COPY.errStale)
    case 'INVALID_LINE_SET':
      return L(BUDGET_EDIT_COPY.errInvalid)
    case 'BUDGET_ALREADY_EXISTS':
      return L(BUDGET_EDIT_COPY.errBudgetExists)
    default:
      return L(BUDGET_EDIT_COPY.errGeneric)
  }
}

interface DraftLine {
  /** Local key — stable across edits so React does not remount an input. */
  key: string
  /** The line of the CURRENT version this one continues; absent = a new line. */
  sourceId?: string
  category: string
  label: string
  amount: string
}

/** What the form hands to its submitter: the canonical line set, already
 *  normalized and already VISIBLE to the user in that form. */
export interface BudgetVersionSubmission {
  baseVersion: number
  lineItems: Array<{ sourceId?: string; category: string; label: string; amount: string }>
}

/** The thin wrapper: auth + network only. Every editing decision lives in the
 *  pure form below, which is why the form can be mounted and driven in a real
 *  browser without Clerk, a router or a query client. */
function BudgetVersionEditor({ projectId, isEs, baseVersion, lineItems, onClose, onSaved }: {
  projectId: string
  isEs: boolean
  baseVersion: number
  lineItems: BudgetItem[]
  onClose: () => void
  onSaved: () => void
}) {
  const { getToken } = useAuth()
  const submit = async (submission: BudgetVersionSubmission): Promise<{ ok: true } | { ok: false; code: string }> => {
    const token = await getToken()
    const res = await apiFetch(`/api/projects/${projectId}/budget/versions`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
      body: JSON.stringify(submission),
    })
    if (res.ok) return { ok: true }
    const body = await res.json().catch(() => ({} as any))
    return { ok: false, code: typeof body?.code === 'string' ? body.code : '' }
  }
  return (
    <BudgetVersionEditorForm
      isEs={isEs}
      baseVersion={baseVersion}
      lineItems={lineItems}
      onSubmit={submit}
      onClose={onClose}
      onSaved={onSaved}
    />
  )
}

// ── D-25AGO-09 (Codex round 2): THE VISIBLE LINE VALUE IS THE PERSISTED ONE ───
//
// The total already agreed; the LINES did not. `canonicalDraft` fed the total
// and the wire, but each input still rendered the RAW draft string — so a user
// typing `0.105` twice saw `0.105` in both boxes, confirmed them, and `0.11`
// was persisted. The confirmation the user gave was for values the system never
// stored.
//
// Two mechanisms, because one is not reliable on its own:
//   · onBlur normalizes the visible value as soon as the field is left — the
//     ordinary path, and the user sees `0.11` immediately;
//   · Save FAILS CLOSED against any line whose visible text is not yet its
//     canonical form. It normalizes the draft, says so, and persists NOTHING.
//     The user saves again, now confirming what will actually be stored.
// Blur order is a browser detail; the save guard does not depend on it.
export function BudgetVersionEditorForm({ isEs, baseVersion, lineItems, onSubmit, onClose, onSaved }: {
  isEs: boolean
  baseVersion: number
  lineItems: BudgetItem[]
  onSubmit: (submission: BudgetVersionSubmission) => Promise<{ ok: true } | { ok: false; code: string }>
  onClose: () => void
  onSaved: () => void
}) {
  const [draft, setDraft] = useState<DraftLine[]>(() =>
    lineItems.map(i => ({ key: i.id, sourceId: i.id, category: i.category, label: i.label, amount: String(i.totalCost) })),
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
  const original = new Map(lineItems.map(i => [i.id, i]))

  const updateLine = (key: string, patch: Partial<DraftLine>) =>
    setDraft(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l)))
  const removeLine = (key: string) => setDraft(prev => prev.filter(l => l.key !== key))
  const addLine = () =>
    setDraft(prev => [...prev, { key: `new-${prev.length}-${String(Date.now())}`, category: 'FRAMING', label: '', amount: '' }])

  // D-25AGO-09 — canonicalize EVERY line ONCE, here. The results drive the delta,
  // the preview total, the per-line hint and the request body, so what the user
  // confirms is byte-for-byte what is sent, accepted and persisted. `Number()` is
  // never the authority: it is lossy, and using it on the wire would let the
  // server round a value the user had already approved.
  const canonicalDraft = draft.map(l => ({ line: l, money: canonicalizeBudgetAmountInput(l.amount) }))
  const invalidLine = canonicalDraft.find(
    c => !c.money.ok || !c.line.category.trim() || !c.line.label.trim(),
  )

  // Delta preview (spec §4.3 heading 3: creating a new version MUST show the
  // delta and the old → new total). Classification mirrors the server's plan and
  // compares CENTS, so a re-rendered 42000 vs "42000.00" is not a false "changed".
  const kept = new Set(draft.map(l => l.sourceId).filter(Boolean) as string[])
  const addedCount = draft.filter(l => !l.sourceId).length
  const changedCount = canonicalDraft.filter(({ line, money }) => {
    if (!line.sourceId) return false
    const src = original.get(line.sourceId)
    if (!src) return false
    const srcMoney = canonicalizeBudgetAmountInput(src.totalCost)
    const sameMoney = money.ok && srcMoney.ok && money.cents === srcMoney.cents
    return src.category !== line.category || src.label !== line.label || !sameMoney
  }).length
  const removedCount = lineItems.filter(i => !kept.has(i.id)).length

  const previousCents = lineItems.reduce((sum, i) => {
    const m = canonicalizeBudgetAmountInput(i.totalCost)
    return m.ok ? sum + m.cents : sum
  }, 0)
  // Preview total in exact integer cents — never a float sum. The server derives
  // its own total from the same canonical strings, so the two cannot disagree.
  const newCents = invalidLine
    ? null
    : sumCents(canonicalDraft.map(c => (c.money.ok ? c.money.cents : 0)))

  const incomplete = Boolean(invalidLine)
  const canSave = draft.length > 0 && !incomplete && !saving

  // Lines whose VISIBLE text is valid money but is not yet its canonical form
  // (`0.105` while the canonical value is `0.11`). These are exactly the lines
  // whose displayed value would otherwise differ from what gets persisted.
  //
  // PR3 (C8): compare VALUES, not text. "1500" and "1500.00" are the same
  // money; only an input with sub-cent precision (that rounding actually
  // changes) is non-canonical. Comparing text made every whole-dollar edit
  // show a false "amounts were rounded" message and need a second Save.
  const nonCanonicalVisible = canonicalDraft.filter(({ line, money }) => money.ok && needsCentRounding(line.amount))

  /** Replace every visible amount with its canonical form. Used by blur and by
   *  the save guard, so both paths normalize identically. */
  const canonicalizeVisibleAmounts = () =>
    setDraft(prev =>
      prev.map(l => {
        const m = canonicalizeBudgetAmountInput(l.amount)
        return m.ok && needsCentRounding(l.amount) ? { ...l, amount: m.amount } : l
      }),
    )

  const save = async () => {
    if (draft.length === 0) {
      setError(L(BUDGET_EDIT_COPY.errEmpty))
      return
    }
    if (incomplete) {
      setError(L(BUDGET_EDIT_COPY.errInvalid))
      return
    }

    // FAIL CLOSED ON A VISIBLE/PERSISTED MISMATCH. Blur normally normalizes the
    // field as the user leaves it, but blur order is a browser detail and Save
    // can be reached without it (keyboard submit, a field still focused, a
    // programmatic click). If any visible amount is not yet canonical, this
    // normalizes the inputs so the user SEES the exact values — and persists
    // NOTHING. The next explicit Save confirms what will actually be stored.
    //
    // Silently normalizing inside the request body instead is precisely the
    // defect this closes: the user would confirm `0.105` and `0.11` would be
    // written, with the divergence visible nowhere.
    if (nonCanonicalVisible.length > 0) {
      canonicalizeVisibleAmounts()
      setError(L(BUDGET_EDIT_COPY.normalizedReviewAgain))
      return
    }

    setSaving(true)
    setError(null)
    try {
      const result = await onSubmit({
        baseVersion,
        // No `totalCost`: the total is DERIVED server-side from these lines.
        // CANONICAL money strings — byte-for-byte the values on screen above.
        lineItems: canonicalDraft.map(({ line, money }) => ({
          ...(line.sourceId ? { sourceId: line.sourceId } : {}),
          category: line.category,
          label: line.label.trim(),
          amount: money.ok ? money.amount : '',
        })),
      })
      if (!result.ok) {
        setError(localizeBudgetVersionError(result.code, isEs))
        return
      }
      onSaved()
    } catch {
      setError(L(BUDGET_EDIT_COPY.errGeneric))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div data-testid="budget-version-editor" className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-[#ECEAE4]">
        <h3 className="text-[13px] font-[800] text-[#1C1A17]">{L(BUDGET_EDIT_COPY.title)}</h3>
        <p className="text-[10px] font-[600] text-[#9E9890] mt-0.5 leading-relaxed">{L(BUDGET_EDIT_COPY.body)}</p>
      </div>

      <div className="divide-y divide-[#F4F2EE]">
        {draft.map(line => (
          <div key={line.key} data-testid="budget-edit-row" className="px-4 py-2.5 space-y-2">
            {/* Stacks at 390px, spreads on wider screens. */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label className="sr-only" htmlFor={`budget-cat-${line.key}`}>{L(BUDGET_EDIT_COPY.categoryLabel)}</label>
              <select
                id={`budget-cat-${line.key}`}
                data-testid={`budget-edit-category-${line.key}`}
                value={line.category}
                onChange={e => updateLine(line.key, { category: e.target.value })}
                className="w-full sm:w-[38%] min-h-[44px] border border-[#ECEAE4] rounded-lg px-3 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
              >
                {Object.entries(BUDGET_CATEGORIES).map(([key, val]) => (
                  <option key={key} value={key}>{isEs ? val.es : val.en}</option>
                ))}
                {/* A stored category outside the known map must remain selectable,
                    or opening the editor would silently retype the line. */}
                {!(line.category in BUDGET_CATEGORIES) && <option value={line.category}>{line.category}</option>}
              </select>

              <label className="sr-only" htmlFor={`budget-label-${line.key}`}>{L(BUDGET_EDIT_COPY.descriptionLabel)}</label>
              <input
                id={`budget-label-${line.key}`}
                data-testid={`budget-edit-label-${line.key}`}
                value={line.label}
                onChange={e => updateLine(line.key, { label: e.target.value })}
                placeholder={L(BUDGET_EDIT_COPY.descriptionLabel)}
                className="w-full sm:flex-1 min-h-[44px] border border-[#ECEAE4] rounded-lg px-3 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
              />

              <label className="sr-only" htmlFor={`budget-amount-${line.key}`}>{L(BUDGET_EDIT_COPY.amountLabel)}</label>
              <input
                id={`budget-amount-${line.key}`}
                data-testid={`budget-edit-amount-${line.key}`}
                type="number"
                step="0.01"
                value={line.amount}
                onChange={e => updateLine(line.key, { amount: e.target.value })}
                // Normalize as soon as the field is left, so the user sees the
                // exact cent value they are about to confirm. The Save guard
                // above does not depend on this having fired.
                onBlur={() => {
                  const money = canonicalizeBudgetAmountInput(line.amount)
                  if (money.ok && money.amount !== line.amount) updateLine(line.key, { amount: money.amount })
                }}
                placeholder={L(BUDGET_EDIT_COPY.amountLabel)}
                className="w-full sm:w-[120px] min-h-[44px] border border-[#ECEAE4] rounded-lg px-3 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
              />

              <button
                type="button"
                data-testid={`budget-edit-remove-${line.key}`}
                onClick={() => removeLine(line.key)}
                aria-label={L(BUDGET_EDIT_COPY.removeLine)}
                className="w-full sm:w-auto min-h-[44px] flex items-center justify-center gap-1.5 rounded-lg border border-[#E7C3C5] px-3 text-[11px] font-[800] text-[#B4232A]"
              >
                <Trash2 size={13} />
                <span className="sm:hidden">{L(BUDGET_EDIT_COPY.removeLine)}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="px-4 py-2.5 border-t border-[#ECEAE4]">
        <button
          type="button"
          data-testid="budget-edit-add-line"
          onClick={addLine}
          className="min-h-[44px] flex items-center gap-1.5 text-[12px] font-[800] text-[#06539B]"
        >
          <Plus size={14} />
          {L(BUDGET_EDIT_COPY.addLine)}
        </button>
      </div>

      {/* Delta + old → new total, shown BEFORE the version is created. */}
      <div data-testid="budget-version-delta" className="px-4 py-3 border-t border-[#ECEAE4] bg-[#FAFAF8] space-y-1">
        <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">{L(BUDGET_EDIT_COPY.deltaTitle)}</div>
        <div className="text-[11px] font-[700] text-[#1C1A17]">
          {addedCount} {L(BUDGET_EDIT_COPY.added)} · {changedCount} {L(BUDGET_EDIT_COPY.changed)} · {removedCount} {L(BUDGET_EDIT_COPY.removed)} {L(BUDGET_EDIT_COPY.lines)}
        </div>
        <div className="flex items-baseline justify-between gap-2 text-[11px] font-[700] text-[#1C1A17]">
          <span>{L(BUDGET_EDIT_COPY.totalFromTo)}</span>
          <span data-testid="budget-version-total-preview" className="font-[900]">
            {formatMoneyFromCents(previousCents)} → {newCents === null ? '—' : formatMoneyFromCents(newCents)}
          </span>
        </div>
        <div className="text-[10px] font-[600] text-[#9E9890]">
          {L(BUDGET_EDIT_COPY.newVersion)}: v{baseVersion} → v{baseVersion + 1}
        </div>
      </div>

      {error && (
        <p data-testid="budget-version-error" className="px-4 pb-2 text-[10px] font-[800] text-[#B4232A]">{error}</p>
      )}

      <div className="px-4 py-3 border-t border-[#ECEAE4] flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          data-testid="budget-version-save-btn"
          disabled={!canSave}
          onClick={save}
          className="w-full min-h-[44px] rounded-full bg-[#06539B] px-3 text-[12px] font-[800] text-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? L(BUDGET_EDIT_COPY.savingBtn) : L(BUDGET_EDIT_COPY.saveCta)}
        </button>
        <button
          type="button"
          data-testid="budget-version-cancel-btn"
          disabled={saving}
          onClick={onClose}
          className="w-full min-h-[44px] rounded-full border border-[#D6CFC4] bg-white px-3 text-[12px] font-[800] text-[#6B655C]"
        >
          {L(BUDGET_EDIT_COPY.cancelCta)}
        </button>
      </div>
    </div>
  )
}

/** PR3 (C8): true only when saving would CHANGE the value (sub-cent input). */
export function needsCentRounding(raw: unknown): boolean {
  const text = typeof raw === 'string' ? raw.trim() : raw
  const exact = parseMoneyToCents(text, { allowNegative: true })
  if (exact.ok) return false
  const rounded = parseMoneyToCents(text, { allowNegative: true, roundSubCent: true })
  return rounded.ok
}
